import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import api from "../api";

const Ctx = createContext(null);

const TTL_MS_DEFAULT = 5 * 60 * 1000; // 5 minutes

export function SuggestedStoriesDiscoveryProvider({ children }) {
  const [users, setUsers] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchedAtRef = useRef(0);
  const inFlightRef = useRef(null);

  const isFresh = useCallback((ttlMs = TTL_MS_DEFAULT) => {
    return users.length > 0 && Date.now() - fetchedAtRef.current < ttlMs;
  }, [users.length]);

  const fetchSuggested = useCallback(async ({
    limit = 12,
    reset = false,
    force = false,
    ttlMs = TTL_MS_DEFAULT,
    keepPrevious = true, // ✅ key: avoids skeleton flash
  } = {}) => {
    // If we already have fresh data, do nothing
    if (!force && !reset && isFresh(ttlMs)) return { users, nextCursor };

    // Dedupe concurrent calls
    if (inFlightRef.current) return inFlightRef.current;

    // If we already have data and we’re keeping previous, don’t flip loading to true
    const shouldShowLoading = !(keepPrevious && users.length > 0);
    if (shouldShowLoading) setLoading(true);
    setError(null);

    const cursor = reset ? null : nextCursor;

    const p = (async () => {
      try {
        const res = await api.get("/suggested/users", {
          params: {
            limit,
            ...(cursor ? { cursor } : {}),
          },
        });

        const raw = Array.isArray(res.data?.users) ? res.data.users : [];
        const mapped = raw.map(u => ({ ...u, suggested: true }));

        setUsers(mapped);
        setNextCursor(res.data?.nextCursor || null);
        fetchedAtRef.current = Date.now();

        return { users: mapped, nextCursor: res.data?.nextCursor || null };
      } catch (e) {
        setError("Failed to load suggested users");
        // Don’t nuke cached users on error
        return { users, nextCursor };
      } finally {
        if (shouldShowLoading) setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = p;
    return p;
  }, [isFresh, users, nextCursor]);

  const clear = useCallback(() => {
    setUsers([]);
    setNextCursor(null);
    setError(null);
    fetchedAtRef.current = 0;
  }, []);

  return (
    <Ctx.Provider value={{ users, nextCursor, loading, error, isFresh, fetchSuggested, clear }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSuggestedStoriesDiscovery() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSuggestedStoriesDiscovery must be used within SuggestedStoriesDiscoveryProvider");
  return ctx;
}
